import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Award, LogIn, X, Users, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { ErrorState } from '../components/common/ErrorState';
import { useCachedData } from '../hooks/useCachedData';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { api } from '../services/api';
import { COMPETITION_LIMITS } from '../config/competitionLimits';
import { CompetitionImage } from '../components/competitions/CompetitionImage';
import type { CompetitionsListData, CompetitionStatus } from '../types';

const STATUS_LABELS: Record<CompetitionStatus, { label: string; className: string }> = {
  registration: { label: 'Inscrições abertas', className: 'bg-turquoise/20 text-turquoise' },
  group_stage: { label: 'Fase de grupos', className: 'bg-sunny/20 text-sunny' },
  knockout: { label: 'Mata-mata', className: 'bg-coral/20 text-coral' },
  finished: { label: 'Encerrado', className: 'bg-white/10 text-white/50' },
  canceled: { label: 'Cancelado', className: 'bg-white/10 text-white/40' },
};

function SkeletonCard() {
  return (
    <div className="glass-card rounded-2xl p-4 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/10 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-4 w-32 bg-white/10 rounded" />
          <div className="h-3 w-24 bg-white/10 rounded" />
        </div>
      </div>
    </div>
  );
}

export function CompetitionsPage() {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const fetcher = useCallback(() => api.competitions.list(), []);
  const { data, isLoading, error, didRefresh, refetch } = useCachedData<CompetitionsListData>({
    cacheKey: 'bt_competitions_list',
    fetcher,
  });
  const competitions = data?.competitions ?? [];
  const limits = data?.limits ?? null;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetch]);

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setIsJoining(true);
    setJoinError(null);
    try {
      const response = await api.competitions.join(joinCode.trim().toUpperCase());
      if (response.success && response.data) {
        navigate(`/tournaments/${response.data.competitionId}`);
      } else {
        setJoinError(response.error?.message || 'Código inválido');
      }
    } catch {
      setJoinError('Erro ao entrar no torneio');
    } finally {
      setIsJoining(false);
    }
  };

  const canCreateMore = limits ? limits.current < limits.max : true;

  return (
    <AppShell
      headerRightElement={
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 text-white/70 hover:text-turquoise hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
          aria-label="Atualizar"
        >
          <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      }
    >
      <div className="space-y-6 max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-2xl font-bold text-white">Torneios</h1>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            size="sm"
            disabled={!canCreateMore || isLoading}
          >
            <Plus className="w-4 h-4" />
            Criar
          </Button>
        </motion.div>

        {/* Join */}
        <Card delay={0.05} className={isLoading ? 'opacity-60 pointer-events-none' : ''}>
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <LogIn className="w-4 h-4 text-turquoise" />
            Entrar em um torneio
          </h3>
          <div className="flex flex-row gap-2">
            <input
              type="text"
              value={joinCode}
              onChange={(e) => {
                setJoinCode(e.target.value.toUpperCase());
                setJoinError(null);
              }}
              placeholder="Código de convite"
              maxLength={COMPETITION_LIMITS.INVITE_CODE_LENGTH}
              className="text-sm flex-1 px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-turquoise/50 focus:ring-1 focus:ring-turquoise/30 font-mono text-center tracking-widest uppercase"
            />
            <Button
              onClick={handleJoin}
              size="sm"
              isLoading={isJoining}
              disabled={!joinCode.trim()}
              noLoadingText
              style={{ minWidth: '74px' }}
            >
              Entrar
            </Button>
          </div>
          {joinError && <p className="text-coral text-sm mt-2">{joinError}</p>}
        </Card>

        {isLoading && (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {!isLoading && error && competitions.length === 0 && (
          <ErrorState message={error} isOffline={!isOnline} onRetry={refetch} />
        )}

        {!isLoading && !error && competitions.length === 0 && (
          <Card delay={0.1}>
            <div className="text-center py-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                <Award className="w-8 h-8 text-white/30" />
              </div>
              <h3 className="font-semibold text-white mb-2">Nenhum torneio ainda</h3>
              <p className="text-sm text-white/50 mb-4">
                Crie um torneio e convide jogadores para disputar a chave!
              </p>
            </div>
          </Card>
        )}

        {!isLoading && competitions.length > 0 && (
          <div className={`space-y-3 rounded-xl ${didRefresh ? 'animate-data-refresh' : ''}`}>
            {competitions.map((c, index) => {
              const status = STATUS_LABELS[c.status];
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                >
                  <button
                    onClick={() => navigate(`/tournaments/${c.id}`)}
                    className="w-full glass-card rounded-2xl p-4 text-left hover:bg-white/[0.08] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <CompetitionImage imageUrl={c.imageUrl} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-white truncate">{c.name}</p>
                          {c.myRole === 'admin' && (
                            <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-turquoise/20 text-turquoise">
                              Admin
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${status.className}`}>
                            {status.label}
                          </span>
                          <span className="text-sm text-white/50 flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {c.playerCount}
                          </span>
                        </div>
                      </div>
                      <div className="text-white/30">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}

      </div>

      <CreateCompetitionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          setShowCreateModal(false);
          refetch();
        }}
      />
    </AppShell>
  );
}

// ============================================================================
// Create Competition Modal
// ============================================================================

function CreateCompetitionModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [thirdPlaceMatch, setThirdPlaceMatch] = useState(false);
  const [poolCount, setPoolCount] = useState<number | null>(null);
  const [teamsPerGroup, setTeamsPerGroup] = useState(3);
  const [teamsAdvancing, setTeamsAdvancing] = useState(2);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    setError(null);
    try {
      const response = await api.competitions.create({
        name: name.trim(),
        description: description.trim() || undefined,
        requiresApproval,
        thirdPlaceMatch,
        poolCount,
        teamsPerGroup,
        teamsAdvancingPerGroup: teamsAdvancing,
      });
      if (response.success && response.data) {
        if (imageBase64) {
          const imgRes = await api.competitions.uploadImage(response.data.id, imageBase64);
          if (!imgRes.success) {
            alert(
              imgRes.error?.message ||
              'Torneio criado, mas não foi possível enviar a imagem. Tente novamente na página do torneio.'
            );
          }
        }
        onCreated();
        navigate(`/tournaments/${response.data.id}`);
      } else {
        setError(response.error?.message || 'Erro ao criar torneio');
      }
    } catch {
      setError('Erro ao criar torneio');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-xl font-bold text-white">Criar torneio</h3>
              <button
                onClick={onClose}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Photo */}
            <div className="flex flex-col items-center mb-5">
              <CompetitionImage
                imageUrl={imageBase64}
                size="lg"
                editable
                onImageSelected={async (b64) => setImageBase64(b64)}
              />
              <p className="text-xs text-white/40 mt-2">
                {imageBase64 ? 'Toque para trocar a foto' : 'Adicionar foto (opcional)'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-white/60 mb-2">Nome do torneio</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Copa de Verão"
                maxLength={COMPETITION_LIMITS.MAX_NAME_LENGTH}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-turquoise/50 focus:ring-1 focus:ring-turquoise/30"
                autoFocus
              />
              <p className="text-xs text-white/30 mt-1 text-right">
                {name.length}/{COMPETITION_LIMITS.MAX_NAME_LENGTH}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-white/60 mb-2">Descrição (opcional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o torneio..."
                maxLength={COMPETITION_LIMITS.MAX_DESCRIPTION_LENGTH}
                rows={2}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-turquoise/50 focus:ring-1 focus:ring-turquoise/30 resize-none"
              />
            </div>

            {/* Requires approval toggle */}
            <div className="mb-4 p-3 rounded-xl bg-white/5">
              <button
                type="button"
                onClick={() => setRequiresApproval((v) => !v)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <span className="text-sm text-white/70">Aprovar inscrições manualmente</span>
                <span
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${requiresApproval ? 'bg-turquoise' : 'bg-white/15'
                    }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${requiresApproval ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                  />
                </span>
              </button>
              <p className="text-[11px] text-white/40 mt-2 leading-snug">
                {requiresApproval
                  ? 'Cada inscrição fica pendente até você aprovar.'
                  : 'Qualquer pessoa com o código entra direto.'}
              </p>
            </div>

            {/* Third-place playoff toggle */}
            <div className="mb-4 p-3 rounded-xl bg-white/5">
              <button
                type="button"
                onClick={() => setThirdPlaceMatch((v) => !v)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <span className="text-sm text-white/70">Disputa de 3º lugar</span>
                <span
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${thirdPlaceMatch ? 'bg-turquoise' : 'bg-white/15'
                    }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${thirdPlaceMatch ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                  />
                </span>
              </button>
              <p className="text-[11px] text-white/40 mt-2 leading-snug">
                {thirdPlaceMatch
                  ? 'Os perdedores das semifinais jogam pelo 3º lugar.'
                  : 'O 3º lugar fica com quem perdeu para o campeão na semifinal.'}
              </p>
            </div>

            {/* Draw mode (free / 2 lists / 3 lists) */}
            <div className="mb-4 p-3 rounded-xl bg-white/5">
              <p className="text-sm text-white/70 mb-2">Sorteio das duplas</p>
              <div className="flex gap-1.5 p-1 rounded-lg bg-white/5">
                {([
                  { v: null, label: 'Livre' },
                  { v: 2, label: '2 listas' },
                  { v: 3, label: '3 listas' },
                ] as const).map((opt) => (
                  <button
                    key={String(opt.v)}
                    type="button"
                    onClick={() => setPoolCount(opt.v)}
                    className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${poolCount === opt.v ? 'bg-turquoise/20 text-turquoise' : 'text-white/50 hover:text-white'
                      }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-white/40 mt-2 leading-snug">
                {poolCount === null
                  ? 'Duplas sorteadas livremente entre todos os jogadores.'
                  : poolCount === 2
                    ? 'Cada dupla terá um jogador da Lista A e um da Lista B.'
                    : 'As duplas priorizam Lista A + Lista C; a Lista B forma duplas entre si ou completa as que faltarem.'}
              </p>
            </div>

            {/* Group sizing */}
            <div className="mb-4 grid grid-cols-2 gap-3">
              <NumberSetting
                label="Duplas por grupo"
                value={teamsPerGroup}
                min={COMPETITION_LIMITS.MIN_TEAMS_PER_GROUP}
                max={COMPETITION_LIMITS.MAX_TEAMS_PER_GROUP}
                onChange={setTeamsPerGroup}
              />
              <NumberSetting
                label="Classificados / grupo"
                value={teamsAdvancing}
                min={COMPETITION_LIMITS.MIN_TEAMS_ADVANCING}
                max={COMPETITION_LIMITS.MAX_TEAMS_ADVANCING}
                onChange={setTeamsAdvancing}
              />
            </div>

            {error && <p className="text-coral text-sm mb-4">{error}</p>}

            <Button onClick={handleCreate} fullWidth isLoading={isCreating} disabled={!name.trim()}>
              <Plus className="w-4 h-4" />
              Criar torneio
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function NumberSetting({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="p-3 rounded-xl bg-white/5">
      <p className="text-xs text-white/60 mb-2">{label}</p>
      <div className="flex items-center justify-between">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-7 h-7 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 flex items-center justify-center"
        >
          −
        </button>
        <span className="text-lg font-bold text-white">{value}</span>
        <button
          onClick={() => onChange(Math.min(max, value + 1))}
          className="w-7 h-7 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 flex items-center justify-center"
        >
          +
        </button>
      </div>
    </div>
  );
}
